import React, { useState, useEffect } from 'react';
import { History, ChevronDown, ChevronUp } from 'lucide-react';
import { d1List } from '@/lib/d1-storage';
import type { ActivityFollowUp, TriedClip } from '@/types';
import type { TriedRatingValue } from '@/data/ratings';

interface ClipExperienceHistoryProps {
  clipId: string;
  coupleId: string;
  tried?: TriedClip[];
  compact?: boolean;
}

const getRatingEmoji = (rating: TriedRatingValue) => {
  switch (rating) {
    case 'hot': return '🔥🔥🔥';
    case 'good': return '🔥🔥';
    case 'meh': return '🔥';
    case 'nope': return '❌';
  }
};

export const ClipExperienceHistory: React.FC<ClipExperienceHistoryProps> = ({
  clipId,
  coupleId,
  tried = [],
  compact = false
}) => {
  const [followUps, setFollowUps] = useState<ActivityFollowUp[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Load activity follow-ups for this clip
  useEffect(() => {
    const loadFollowUps = async () => {
      try {
        const allFollowUps = await d1List<ActivityFollowUp>('activity_followups', { coupleId });
        const clipFollowUps = allFollowUps
          .filter(f => f.clipId === clipId && f.didComplete)
          .sort((a, b) => (b.respondedAt || '').localeCompare(a.respondedAt || ''));
        setFollowUps(clipFollowUps);
      } catch {
        setFollowUps([]);
      } finally {
        setLoading(false);
      }
    };
    loadFollowUps();
  }, [clipId, coupleId]);

  // Also check tried clips for this clip
  const triedHistory = tried.filter(t => t.clipId === clipId);

  // Combine and dedupe experiences (prefer follow-ups as they have more detail)
  const followUpDates = new Set(followUps.map(f => f.scheduledDate));
  const uniqueTriedHistory = triedHistory.filter(t => {
    // Only include tried clips that aren't already in follow-ups
    const triedDate = t.triedAt.split('T')[0] ?? '';
    return !followUpDates.has(triedDate);
  });

  const totalExperiences = followUps.length + uniqueTriedHistory.length;

  if (loading) return null;
  if (totalExperiences === 0) return null;

  // Compact mode for scheduling modal
  if (compact) {
    return (
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-amber-400" />
          <span className="text-amber-400 text-sm font-medium">
            You've done this {totalExperiences} time{totalExperiences > 1 ? 's' : ''} before
          </span>
        </div>
        {followUps[0] && (
          <p className="text-zinc-400 text-xs mt-1">
            Last: {new Date(followUps[0].scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            {followUps[0].hisReflection?.rating && (
              <span className="ml-2">{getRatingEmoji(followUps[0].hisReflection.rating)}</span>
            )}
          </p>
        )}
        {!followUps[0] && triedHistory[0] && (
          <p className="text-zinc-400 text-xs mt-1">
            Last: {new Date(triedHistory[0].triedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            <span className="ml-2">{getRatingEmoji(triedHistory[0].rating)}</span>
          </p>
        )}
      </div>
    );
  }

  // Full mode for clip detail view
  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <History className="w-5 h-5 text-lime-400" />
        <h3 className="text-white font-medium">Your Past Experiences</h3>
        <span className="text-zinc-500 text-sm">({totalExperiences})</span>
      </div>

      <div className="space-y-2">
        {/* Activity follow-ups (detailed) */}
        {followUps.map(followUp => (
          <div key={followUp.id} className="bg-zinc-700/50 rounded-lg overflow-hidden">
            <button
              onClick={() => setExpanded(expanded === followUp.id ? null : followUp.id)}
              className="w-full px-3 py-2 flex items-center justify-between hover:bg-zinc-700/70 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-zinc-400 text-sm">
                  {new Date(followUp.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                {followUp.hisReflection?.rating && (
                  <span className="text-lg">{getRatingEmoji(followUp.hisReflection.rating)}</span>
                )}
                {followUp.herReflection?.rating && followUp.herReflection.rating !== followUp.hisReflection?.rating && (
                  <span className="text-lg">{getRatingEmoji(followUp.herReflection.rating)}</span>
                )}
              </div>
              {expanded === followUp.id ? (
                <ChevronUp className="w-4 h-4 text-zinc-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-zinc-400" />
              )}
            </button>

            {expanded === followUp.id && (
              <div className="px-3 pb-3 space-y-3 border-t border-zinc-600">
                {/* His reflection */}
                {followUp.hisReflection && (
                  <div className="pt-3">
                    <p className="text-blue-400 text-xs font-medium mb-1">His thoughts</p>
                    {followUp.hisReflection.howItWent && (
                      <p className="text-zinc-300 text-sm mb-1">"{followUp.hisReflection.howItWent}"</p>
                    )}
                    {followUp.hisReflection.differenceFromClip && (
                      <p className="text-zinc-400 text-xs">
                        <span className="text-zinc-500">vs clip:</span> {followUp.hisReflection.differenceFromClip}
                      </p>
                    )}
                    {followUp.hisReflection.nextTimeDifferently && (
                      <p className="text-zinc-400 text-xs">
                        <span className="text-zinc-500">next time:</span> {followUp.hisReflection.nextTimeDifferently}
                      </p>
                    )}
                  </div>
                )}

                {/* Her reflection */}
                {followUp.herReflection && (
                  <div>
                    <p className="text-pink-400 text-xs font-medium mb-1">Her thoughts</p>
                    {followUp.herReflection.howItWent && (
                      <p className="text-zinc-300 text-sm mb-1">"{followUp.herReflection.howItWent}"</p>
                    )}
                    {followUp.herReflection.differenceFromClip && (
                      <p className="text-zinc-400 text-xs">
                        <span className="text-zinc-500">vs clip:</span> {followUp.herReflection.differenceFromClip}
                      </p>
                    )}
                    {followUp.herReflection.nextTimeDifferently && (
                      <p className="text-zinc-400 text-xs">
                        <span className="text-zinc-500">next time:</span> {followUp.herReflection.nextTimeDifferently}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Tried clips (simpler, from legacy system) */}
        {uniqueTriedHistory.map((tried, idx) => (
          <div key={`tried-${idx}`} className="bg-zinc-700/50 rounded-lg overflow-hidden">
            <button
              onClick={() => setExpanded(expanded === `tried-${idx}` ? null : `tried-${idx}`)}
              className="w-full px-3 py-2 flex items-center justify-between hover:bg-zinc-700/70 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-zinc-400 text-sm">
                  {new Date(tried.triedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span className="text-lg">{getRatingEmoji(tried.rating)}</span>
              </div>
              {expanded === `tried-${idx}` ? (
                <ChevronUp className="w-4 h-4 text-zinc-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-zinc-400" />
              )}
            </button>

            {expanded === `tried-${idx}` && (
              <div className="px-3 pb-3 space-y-2 border-t border-zinc-600 pt-3">
                {tried.hisThoughts && (
                  <div>
                    <p className="text-blue-400 text-xs font-medium mb-1">His thoughts</p>
                    <p className="text-zinc-300 text-sm">"{tried.hisThoughts}"</p>
                  </div>
                )}
                {tried.herThoughts && (
                  <div>
                    <p className="text-pink-400 text-xs font-medium mb-1">Her thoughts</p>
                    <p className="text-zinc-300 text-sm">"{tried.herThoughts}"</p>
                  </div>
                )}
                {tried.wouldDoAgain && (
                  <p className="text-zinc-400 text-xs">
                    Would do again: {tried.wouldDoAgain === 'yes' ? '✅ Yes' : tried.wouldDoAgain === 'maybe' ? '🤔 Maybe' : '❌ No'}
                  </p>
                )}
                {tried.howDifferent && (
                  <p className="text-zinc-400 text-xs">
                    <span className="text-zinc-500">Inspired differently:</span> {tried.howDifferent}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
